import { useMemo, useState, useEffect } from "react";
import { EnvelopeSimpleIcon as EnvelopeSimple, CaretDownIcon as CaretDown, CaretUpIcon as CaretUp } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const LS_FILTERS = "attivita_table_filters_v1";
const LS_SELECTION = "attivita_table_selection_v1";
const LS_COLW = "attivita_table_colw_v1";
const DEFAULT_COLW = { select: 40, numero: 90, cliente: 220, start: 140, end: 140, stato: 130, modalita: 150, tecnici: 200, inviata: 100, contratto: 160, ore: 90 };
const _loadF = () => { try { return JSON.parse(localStorage.getItem(LS_FILTERS)) || {}; } catch { return {}; } };
const _loadSel = () => { try { return new Set(JSON.parse(localStorage.getItem(LS_SELECTION)) || []); } catch { return new Set(); } };
const _loadW = () => { try { return { ...DEFAULT_COLW, ...(JSON.parse(localStorage.getItem(LS_COLW)) || {}) }; } catch { return { ...DEFAULT_COLW }; } };

const validSess = (s) => s && s.data && s.ora_inizio && s.ora_fine && s.ora_fine > s.ora_inizio;

// yyyy-mm-dd HH:MM string ordinabile a partire dalle sessioni valide (fallback data_inizio/richiesta/created_at)
function boundsOf(a) {
  const vs = (a.sessioni_lavoro || []).filter(validSess);
  if (vs.length) {
    const starts = vs.map((s) => `${s.data}T${s.ora_inizio}`).sort();
    const ends = vs.map((s) => `${s.data}T${s.ora_fine}`).sort();
    return { start: starts[0], end: ends[ends.length - 1] };
  }
  const di = a.data_inizio || a.data_richiesta || a.created_at || "";
  const df = a.data_fine || di;
  return { start: di ? String(di) : "", end: df ? String(df) : "" };
}

function fmtDT(iso) {
  if (!iso) return "—";
  const s = String(iso);
  const date = s.slice(0, 10);
  const [y, m, d] = date.split("-");
  const time = s.length >= 16 ? s.slice(11, 16) : "";
  const dmy = d && m && y ? `${d}/${m}/${y}` : date;
  return time ? `${dmy} ${time}` : dmy;
}

const fmtOre = (min) => `${((min || 0) / 60).toFixed(2)}`.replace(".", ",");

export function AttivitaTable({ items, tecnici, statiCfg, modalitaCfg, statoColor, statoLabel, modalitaLabel, onRowClick, onBulkDone }) {
  const _pf = _loadF();
  const [fNum, setFNum] = useState(_pf.fNum || "");
  const [fCli, setFCli] = useState(_pf.fCli || "");
  const [fFrom, setFFrom] = useState(_pf.fFrom || "");
  const [fTo, setFTo] = useState(_pf.fTo || "");
  const [fStato, setFStato] = useState(_pf.fStato || "");
  const [fMod, setFMod] = useState(_pf.fMod || "");
  const [fTec, setFTec] = useState(_pf.fTec || "");
  const [fInviata, setFInviata] = useState(_pf.fInviata || "");
  const [fContr, setFContr] = useState(_pf.fContr || "");
  const [sortKey, setSortKey] = useState(_pf.sortKey || "start");
  const [sortDir, setSortDir] = useState(_pf.sortDir || "desc");
  const [selected, setSelected] = useState(_loadSel);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    localStorage.setItem(LS_FILTERS, JSON.stringify({ fNum, fCli, fFrom, fTo, fStato, fMod, fTec, fInviata, fContr, sortKey, sortDir }));
  }, [fNum, fCli, fFrom, fTo, fStato, fMod, fTec, fInviata, fContr, sortKey, sortDir]);

  useEffect(() => {
    localStorage.setItem(LS_SELECTION, JSON.stringify([...selected]));
  }, [selected]);

  const hasFilters = !!(fNum || fCli || fFrom || fTo || fStato || fMod || fTec || fInviata || fContr);
  const resetFiltri = () => { setFNum(""); setFCli(""); setFFrom(""); setFTo(""); setFStato(""); setFMod(""); setFTec(""); setFInviata(""); setFContr(""); };

  const [colW, setColW] = useState(_loadW);
  useEffect(() => { localStorage.setItem(LS_COLW, JSON.stringify(colW)); }, [colW]);
  const totalW = Object.values(colW).reduce((a, b) => a + b, 0);
  const startResize = (key, e) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX; const startW = colW[key] || 100;
    const onMove = (ev) => { const w = Math.max(50, startW + (ev.clientX - startX)); setColW((c) => ({ ...c, [key]: w })); };
    const onUp = () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); document.body.style.userSelect = ""; };
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove); document.addEventListener("mouseup", onUp);
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const rows = useMemo(() => {
    const q = (v) => (v || "").toString().toLowerCase();
    const mapped = items.map((a) => {
      const b = boundsOf(a);
      const inviata = !!(a.rapportino_inviato_at || (a.rapportino_invii || []).length);
      const contratto = a.support_contract_nome || a.contratto_nome || "";
      const tecnici_nomi = a.tecnico_nome || (a.tecnici || []).map((t) => t.name).join(", ");
      return { a, start: b.start, end: b.end, inviata, contratto, tecnici_nomi };
    });
    const filtered = mapped.filter((r) => {
      const a = r.a;
      if (fNum && !q(a.numero).includes(q(fNum))) return false;
      if (fCli && !q(a.cliente_nome).includes(q(fCli))) return false;
      if (fStato && a.stato !== fStato) return false;
      if (fMod && a.modalita_addebito !== fMod) return false;
      if (fTec) {
        const ids = [...(a.tecnici_ids || []), ...(a.sessioni_lavoro || []).map((s) => s.tecnico_id)];
        if (!ids.includes(fTec)) return false;
      }
      if (fInviata === "si" && !r.inviata) return false;
      if (fInviata === "no" && r.inviata) return false;
      if (fContr && !q(r.contratto).includes(q(fContr))) return false;
      const sd = (r.start || "").slice(0, 10);
      const ed = (r.end || "").slice(0, 10);
      if (fFrom && sd !== fFrom) return false;
      if (fTo && ed !== fTo) return false;
      return true;
    });

    // valore + tipo per la colonna di ordinamento
    const COLS = {
      numero: { type: "text", get: (r) => r.a.numero },
      cliente: { type: "text", get: (r) => r.a.cliente_nome },
      start: { type: "text", get: (r) => r.start },
      end: { type: "text", get: (r) => r.end },
      stato: { type: "text", get: (r) => statoLabel(r.a.stato) },
      modalita: { type: "text", get: (r) => modalitaLabel(r.a.modalita_addebito) },
      tecnici: { type: "text", get: (r) => r.tecnici_nomi },
      inviata: { type: "bool", get: (r) => r.inviata },
      contratto: { type: "text", get: (r) => r.contratto },
      ore: { type: "number", get: (r) => r.a.tempo_totale_minuti },
    };
    const col = COLS[sortKey] || COLS.start;
    const cmp = (ra, rb) => {
      const va = col.get(ra), vb = col.get(rb);
      const ea = va === null || va === undefined || va === "";
      const eb = vb === null || vb === undefined || vb === "";
      if (ea && eb) return 0;
      if (ea) return 1;   // vuoti sempre in fondo (ASC e DESC)
      if (eb) return -1;
      let res;
      if (col.type === "number") res = Number(va) - Number(vb);
      else if (col.type === "bool") res = va === vb ? 0 : va ? 1 : -1;
      else res = String(va).localeCompare(String(vb), "it", { numeric: true, sensitivity: "base" });
      return sortDir === "asc" ? res : -res;
    };
    filtered.sort(cmp);
    return filtered;
  }, [items, fNum, fCli, fFrom, fTo, fStato, fMod, fTec, fInviata, fContr, sortKey, sortDir, statoLabel, modalitaLabel]);

  const th = "px-2 py-2 text-left font-semibold text-xs uppercase tracking-wide text-muted-foreground border-b border-border whitespace-nowrap cursor-pointer select-none hover:text-foreground bg-secondary/50";
  const visibleIds = rows.map((r) => r.a.id);
  const allSel = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSel = visibleIds.some((id) => selected.has(id));
  const toggleAll = () => setSelected((s) => { const n = new Set(s); if (allSel) visibleIds.forEach((id) => n.delete(id)); else visibleIds.forEach((id) => n.add(id)); return n; });
  const toggleOne = (id, e) => { e.stopPropagation(); setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const filterCell = "px-2 pb-2 align-top border-b border-border bg-background";
  const inpCls = "w-full h-8 rounded-sm border border-border bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary";
  const td = "px-2 py-2 text-sm border-b border-border align-top overflow-hidden";
  const arrow = (key) => (sortKey === key ? (sortDir === "asc" ? <CaretUp size={12} /> : <CaretDown size={12} />) : <span className="opacity-25"><CaretDown size={12} /></span>);
  const ResizeHandle = ({ col }) => (
    <span onMouseDown={(e) => startResize(col, e)} onClick={(e) => e.stopPropagation()} className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-500/60 active:bg-blue-600" data-testid={`resize-${col}`} title="Trascina per ridimensionare la colonna" />
  );
  const Head = ({ col, label, align }) => (
    <th className={`${th} relative ${align === "right" ? "text-right" : ""}`} onClick={() => toggleSort(col)} data-testid={`sort-${col}`}>
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""}`}>{label} {arrow(col)}</span>
      <ResizeHandle col={col} />
    </th>
  );

  const bulkStato = async (stato) => {
    if (!stato) return; setBusy(true);
    try { const { data } = await api.post("/attivita/bulk/stato", { ids: [...selected], stato }); toast.success(`Stato aggiornato su ${data.modificati} intervent${data.modificati === 1 ? "o" : "i"}`); setSelected(new Set()); onBulkDone && onBulkDone(); }
    catch { toast.error("Errore durante l'aggiornamento dello stato"); } finally { setBusy(false); }
  };
  const bulkTecnico = async (tid) => {
    if (!tid) return; setBusy(true);
    try { const { data } = await api.post("/attivita/bulk/tecnico", { ids: [...selected], tecnico_id: tid }); toast.success(`Tecnico assegnato su ${data.modificati} intervent${data.modificati === 1 ? "o" : "i"}`); setSelected(new Set()); onBulkDone && onBulkDone(); }
    catch { toast.error("Errore durante l'assegnazione del tecnico"); } finally { setBusy(false); }
  };
  const exportCsv = () => {
    const sel = rows.filter((r) => selected.has(r.a.id));
    const head = ["Numero", "Cliente", "Data inizio", "Data fine", "Stato", "Tipologia", "Tecnici", "Inviata", "Contratto", "Ore"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = sel.map((r) => [r.a.numero, r.a.cliente_nome, fmtDT(r.start), fmtDT(r.end), statoLabel(r.a.stato), modalitaLabel(r.a.modalita_addebito), r.tecnici_nomi, r.inviata ? "Sì" : "No", r.contratto, fmtOre(r.a.tempo_totale_minuti)].map(esc).join(";"));
    const csv = [head.map(esc).join(";"), ...lines].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob); const link = document.createElement("a");
    link.href = url; link.download = `interventi_${new Date().toISOString().slice(0, 10)}.csv`; link.click(); URL.revokeObjectURL(url);
  };
  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.post("/attivita/bulk/pdf", { ids: [...selected] }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url; link.download = `interventi_${new Date().toISOString().slice(0, 10)}.pdf`; link.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Esportazione PDF non riuscita"); }
    finally { setPdfBusy(false); }
  };
  const selCls = "h-8 rounded-sm border border-border bg-background text-foreground px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50";

  return (
    <>
    {selected.size > 0 && (
      <div className="flex flex-wrap items-center gap-2 mb-3 px-3 py-2.5 rounded-md border-l-4 border-l-primary bg-primary text-primary-foreground shadow-md animate-in fade-in slide-in-from-top-1 duration-200" data-testid="attivita-bulk-bar">
        <span className="inline-flex items-center gap-1.5 text-sm font-bold">
          <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-primary-foreground text-primary text-xs font-bold">{selected.size}</span>
          selezionat{selected.size === 1 ? "o" : "i"}
        </span>
        <span className="h-5 w-px bg-primary-foreground/30 mx-1" />
        <select className={`${selCls} w-48`} disabled={busy} value="" onChange={(e) => { bulkTecnico(e.target.value); e.target.value = ""; }} data-testid="bulk-tecnico">
          <option value="">Assegna tecnico…</option>
          {tecnici.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select className={`${selCls} w-48`} disabled={busy} value="" onChange={(e) => { bulkStato(e.target.value); e.target.value = ""; }} data-testid="bulk-stato">
          <option value="">Cambia stato…</option>
          {statiCfg.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <button type="button" className="h-8 rounded-sm bg-primary-foreground px-3 text-xs font-semibold text-primary hover:bg-primary-foreground/90 transition-colors" onClick={exportCsv} data-testid="bulk-export">Esporta CSV</button>
        <button type="button" className="h-8 rounded-sm bg-primary-foreground px-3 text-xs font-semibold text-primary hover:bg-primary-foreground/90 transition-colors disabled:opacity-50" onClick={exportPdf} disabled={pdfBusy} data-testid="bulk-export-pdf">{pdfBusy ? "PDF…" : "Esporta PDF"}</button>
        <button type="button" className="h-8 rounded-sm px-2.5 text-xs font-medium text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/15 transition-colors ml-auto" onClick={() => setSelected(new Set())} data-testid="bulk-clear">Deseleziona</button>
      </div>
    )}
    <div className="flex items-center justify-end mb-2">
      <button type="button" className="h-8 rounded-sm bg-blue-600 px-3 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap shadow-sm" disabled={!hasFilters} onClick={resetFiltri} data-testid="attivita-reset-filtri" title="Reset filtri">Reset filtri</button>
    </div>
    <div className="overflow-auto rounded-sm border border-border max-h-[calc(100vh-18rem)]" data-testid="attivita-table">
      <table className="border-collapse" style={{ tableLayout: "fixed", width: totalW }}>
        <colgroup>
          <col style={{ width: colW.select }} />
          <col style={{ width: colW.numero }} />
          <col style={{ width: colW.cliente }} />
          <col style={{ width: colW.start }} />
          <col style={{ width: colW.end }} />
          <col style={{ width: colW.stato }} />
          <col style={{ width: colW.modalita }} />
          <col style={{ width: colW.tecnici }} />
          <col style={{ width: colW.inviata }} />
          <col style={{ width: colW.contratto }} />
          <col style={{ width: colW.ore }} />
        </colgroup>
        <thead className="bg-secondary/50 sticky top-0 z-10">
          <tr>
            <th className="px-2 py-2 w-9 border-b border-border text-center bg-secondary/50"><input type="checkbox" checked={allSel} ref={(el) => { if (el) el.indeterminate = !allSel && someSel; }} onChange={toggleAll} data-testid="attivita-select-all" title="Seleziona tutti" /></th>
            <Head col="numero" label="Numero" />
            <Head col="cliente" label="Cliente" />
            <Head col="start" label="Data inizio" />
            <Head col="end" label="Data fine" />
            <Head col="stato" label="Stato" />
            <Head col="modalita" label="Tipologia" />
            <Head col="tecnici" label="Tecnici" />
            <Head col="inviata" label="Inviata" />
            <Head col="contratto" label="Contratto" />
            <Head col="ore" label="Ore" align="right" />
          </tr>
          <tr className="bg-background">
            <td className={filterCell}></td>
            <td className={filterCell}><input className={inpCls} placeholder="Filtra…" value={fNum} onChange={(e) => setFNum(e.target.value)} data-testid="tf-numero" /></td>
            <td className={filterCell}><input className={inpCls} placeholder="Filtra…" value={fCli} onChange={(e) => setFCli(e.target.value)} data-testid="tf-cliente" /></td>
            <td className={filterCell}><input type="date" className={inpCls} value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="tf-data-da" title="Filtra per data inizio esatta" /></td>
            <td className={filterCell}><input type="date" className={inpCls} value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="tf-data-a" title="Filtra per data fine esatta" /></td>
            <td className={filterCell}>
              <select className={inpCls} value={fStato} onChange={(e) => setFStato(e.target.value)} data-testid="tf-stato">
                <option value="">Tutti</option>
                {statiCfg.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </td>
            <td className={filterCell}>
              <select className={inpCls} value={fMod} onChange={(e) => setFMod(e.target.value)} data-testid="tf-modalita">
                <option value="">Tutte</option>
                {modalitaCfg.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </td>
            <td className={filterCell}>
              <select className={inpCls} value={fTec} onChange={(e) => setFTec(e.target.value)} data-testid="tf-tecnico">
                <option value="">Tutti</option>
                {tecnici.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </td>
            <td className={filterCell}>
              <select className={inpCls} value={fInviata} onChange={(e) => setFInviata(e.target.value)} data-testid="tf-inviata">
                <option value="">Tutti</option>
                <option value="si">Inviata</option>
                <option value="no">Non inviata</option>
              </select>
            </td>
            <td className={filterCell}><input className={inpCls} placeholder="Filtra…" value={fContr} onChange={(e) => setFContr(e.target.value)} data-testid="tf-contratto" /></td>
            <td className={filterCell}></td>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={11} className="px-3 py-10 text-center text-sm text-muted-foreground" data-testid="attivita-table-empty">Nessun intervento corrisponde ai filtri.</td></tr>
          ) : rows.map((r) => {
            const a = r.a;
            const color = statoColor(a.stato);
            return (
              <tr key={a.id} onClick={() => onRowClick(a.id)} className={`cursor-pointer transition-colors ${selected.has(a.id) ? "bg-primary/5 hover:bg-primary/10" : "hover:bg-secondary/40"}`} data-testid={`attivita-row-${a.id}`}>
                <td className={td} onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selected.has(a.id)} onChange={(e) => toggleOne(a.id, e)} data-testid={`attivita-select-${a.id}`} /></td>
                <td className={`${td} font-mono font-medium`}>{a.numero || "—"}</td>
                <td className={td}>{a.cliente_nome || <span className="text-muted-foreground">Non assegnato</span>}</td>
                <td className={`${td} whitespace-nowrap`}>{fmtDT(r.start)}</td>
                <td className={`${td} whitespace-nowrap`}>{fmtDT(r.end)}</td>
                <td className={td}>
                  <span className="inline-block text-[11px] px-1.5 py-0.5 rounded-sm whitespace-nowrap" style={{ backgroundColor: `${color}22`, color }}>{statoLabel(a.stato)}</span>
                </td>
                <td className={`${td} whitespace-nowrap`}>{modalitaLabel(a.modalita_addebito)}</td>
                <td className={td}>{r.tecnici_nomi || <span className="text-muted-foreground">—</span>}</td>
                <td className={td}>
                  {r.inviata ? (
                    <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700 whitespace-nowrap" data-testid="badge-inviata"><EnvelopeSimple size={12} weight="fill" /> Inviata</span>
                  ) : <span className="text-muted-foreground text-xs">—</span>}
                </td>
                <td className={td}>{r.contratto || <span className="text-muted-foreground">—</span>}</td>
                <td className={`${td} text-right font-medium whitespace-nowrap`}>{fmtOre(a.tempo_totale_minuti)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border bg-secondary/30 flex items-center gap-3" data-testid="attivita-table-count">
        <span>{rows.length} intervent{rows.length === 1 ? "o" : "i"}</span>
        {selected.size > 0 && (<><span className="text-primary font-medium" data-testid="attivita-selected-count">· {selected.size} selezionat{selected.size === 1 ? "o" : "i"}</span><button type="button" className="text-muted-foreground hover:text-destructive underline" onClick={() => setSelected(new Set())} data-testid="attivita-clear-selection">Deseleziona</button></>)}
      </div>
    </div>
    </>
  );
}
